/**
 * Tài khoản nhận tiền CỦA ADDY — API của console nền tảng (#18969).
 * Real /api/v1/platform/saas-merchant.
 *
 * Đây là tiền phòng khám trả cho Addy, không phải tiền bệnh nhân trả cho phòng khám. Hai luồng
 * dùng chung nhà cung cấp (PayOS) nhưng khác hẳn tài khoản, khác hẳn quyền: luồng bệnh nhân cấu
 * hình theo từng phòng khám, luồng này chỉ quản trị viên nền tảng chạm được.
 *
 * API **không bao giờ** trả giá trị bí mật — chỉ "đã có hay chưa". Ô để trống khi lưu nghĩa là giữ
 * nguyên, vì không ai gõ lại được thứ họ không nhìn thấy.
 */
import { apiClient } from "./client";

export type SaasMerchantStatus = "DRAFT" | "ACTIVE" | "DISABLED";

export type SaasGatewayProvider = "PAYOS" | "SEPAY";

export interface SaasMerchantRow {
  id: string;
  provider: SaasGatewayProvider;
  clientId: string | null;
  /** Chỉ SePay: tiền tố mã chuyển khoản, phải khớp cấu hình bên my.sepay.vn. */
  codePrefix: string | null;
  hasApiKey: boolean;
  hasChecksumKey: boolean;
  baseUrl: string;
  returnUrl: string | null;
  cancelUrl: string | null;
  status: SaasMerchantStatus;
  activatedAt: string | null;
  updatedAt: string | null;
}

/** Ô để trống = giữ nguyên. */
export interface SaasMerchantUpdate {
  clientId?: string;
  apiKey?: string;
  checksumKey?: string;
  baseUrl?: string;
  returnUrl?: string;
  cancelUrl?: string;
  /** Bỏ trống khi tạo mới = PAYOS. */
  provider?: SaasGatewayProvider;
  codePrefix?: string;
}

/** Kết quả gọi thử PayOS, bằng đúng lời mà màn hình sẽ in ra. */
export interface SaasMerchantConnectionCheck {
  ok: boolean;
  detail: string;
}

const BASE = "/v1/platform/saas-merchant";

export const platformSaasMerchantApi = {
  list(): Promise<SaasMerchantRow[]> {
    return apiClient.get<SaasMerchantRow[]>(BASE).then((r) => r.data);
  },
  create(body: SaasMerchantUpdate): Promise<SaasMerchantRow> {
    return apiClient.post<SaasMerchantRow>(BASE, body).then((r) => r.data);
  },
  update(id: string, body: SaasMerchantUpdate): Promise<SaasMerchantRow> {
    return apiClient.put<SaasMerchantRow>(`${BASE}/${id}`, body).then((r) => r.data);
  },
  /**
   * Gọi thử PayOS bằng đúng bộ khoá đã lưu. PayOS không có endpoint "ping", nên phép thử là tạo
   * một đơn 2.000đ — không ai trả, tự hết hạn. Đó là cách duy nhất chứng minh khoá ký được.
   */
  test(id: string): Promise<SaasMerchantConnectionCheck> {
    return apiClient
      .post<SaasMerchantConnectionCheck>(`${BASE}/${id}/test`)
      .then((r) => r.data);
  },
  activate(id: string): Promise<SaasMerchantRow> {
    return apiClient.post<SaasMerchantRow>(`${BASE}/${id}/activate`).then((r) => r.data);
  },
  disable(id: string): Promise<SaasMerchantRow> {
    return apiClient.post<SaasMerchantRow>(`${BASE}/${id}/disable`).then((r) => r.data);
  },
};

export const platformSaasMerchantQueryKeys = {
  all: () => ["platform-saas-merchant"] as const,
};

// ================================================= Tài khoản ngân hàng nhận tiền

/**
 * Tài khoản Addy nhận tiền — RIÊNG khỏi cấu hình cổng thanh toán (#18990).
 *
 * Tách ra vì **chuyển khoản tay không đi qua cổng nào**. Phòng khám không dùng thanh toán trực
 * tuyến vẫn phải đọc được số tài khoản để chuyển, nên nó không được phụ thuộc vào việc có khai
 * PayOS hay SePay hay không. SePay cũng đọc chính tài khoản này để dựng mã QR, nên số in trên QR
 * và số nhân viên đọc cho khách luôn là một.
 *
 * Không có bí mật nào ở đây: số tài khoản vốn để đưa cho người trả tiền.
 */
export interface SaasBankAccount {
  id: string;
  bankName: string;
  accountNo: string;
  accountName: string | null;
  note: string | null;
  status: "ACTIVE" | "DISABLED";
  updatedAt: string | null;
}

export interface SaasBankAccountSave {
  bankName: string;
  accountNo: string;
  accountName?: string;
  note?: string;
}

const BANK = "/v1/platform/saas-bank-account";

export const platformBankAccountApi = {
  list(): Promise<SaasBankAccount[]> {
    return apiClient.get<SaasBankAccount[]>(BANK).then((r) => r.data);
  },
  create(body: SaasBankAccountSave): Promise<SaasBankAccount> {
    return apiClient.post<SaasBankAccount>(BANK, body).then((r) => r.data);
  },
  update(id: string, body: SaasBankAccountSave): Promise<SaasBankAccount> {
    return apiClient.put<SaasBankAccount>(`${BANK}/${id}`, body).then((r) => r.data);
  },
  /** Bật tài khoản này và tắt mọi tài khoản khác — một nơi nhận tiền tại một thời điểm. */
  activate(id: string): Promise<SaasBankAccount> {
    return apiClient.post<SaasBankAccount>(`${BANK}/${id}/activate`).then((r) => r.data);
  },
};

export const platformBankAccountQueryKeys = {
  all: () => ["platform-bank-account"] as const,
};
